import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilter from '@/components/DateRangeFilter';
import DebouncedSearchBar from '@/components/DebouncedSearchBar';
import { Option, StyledSelect } from '@/components/Forms/Dropdown';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import { TableContent, Td, Th } from '@/components/Table';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import {
  ChannelGroupResource,
  DQCascadeItemResource,
  DQCascadeRunResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';

import { Body, StyledTable, Toolbar } from './Parts';
import {
  ItemStatusBadge,
  RUNNING_REFETCH_INTERVAL,
  dqCascadeQueryKeys,
  formatDate,
  formatSkipReason,
  formatTime,
} from './helpers';

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Failed', value: 'failed' },
  { label: 'Inserted', value: 'inserted' },
  { label: 'Skipped', value: 'skipped' },
  { label: 'Pending', value: 'pending,processing' },
];

const Filters = styled.div`
  flex-basis: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchHolder = styled.div`
  width: 220px;
  > div {
    margin: 0;
  }
`;

const PageSelect = styled(StyledSelect)`
  width: 220px;
  .react-select__control {
    height: 40px;
    min-height: 40px;
  }
`;

const ItemsTableLayout = styled(StyledTable)`
  table-layout: fixed;

  th,
  td {
    &:nth-child(1) {
      width: 38%;
    }

    &:nth-child(4) {
      width: 24%;
    }

    &:last-child {
      width: 104px;
    }
  }
`;

const PostMessage = styled(Text).attrs({ $type: 'smallBold' })`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const PostMeta = styled(Text).attrs({ $type: 'extraSmall', $color: 'gray' })`
  margin-top: 4px;
  a {
    color: ${({ theme }) => theme.colors.purple};
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StatusDetails = styled(Text).attrs({
  $type: 'extraSmall',
  $color: 'gray',
})`
  margin-top: 6px;
  word-break: break-word;
`;

interface Props {
  controls: ReactNode;
  group: ChannelGroupResource;
  groupNumber: number;
  run: DQCascadeRunResource | null;
  onClearRun: VoidFunction;
}

const ItemsTable: FC<Props> = ({
  controls,
  group,
  groupNumber,
  run,
  onClearRun,
}) => {
  const [status, setStatus] = useState('');
  const [channelId, setChannelId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});
  const [retryingItemId, setRetryingItemId] = useState<number | null>(null);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const dateFrom = (dateRange.rangeStart ?? dateRange.rangeEnd)?.toISODate();
  const dateTo = (dateRange.rangeEnd ?? dateRange.rangeStart)?.toISODate();

  const filters = {
    groupNumber,
    runId: run?.id,
    status,
    channelId,
    search: search.trim(),
    dateFrom,
    dateTo,
  };

  const fetchItems = ({ pageParam = '/v1/editorial/queue/dq-cascade/items' }) =>
    api.get<never, PaginatedResponse<DQCascadeItemResource>>(pageParam, {
      params: {
        'filter[groupNumber]': groupNumber,
        ...(!!filters.runId && { 'filter[runId]': filters.runId }),
        ...(!!filters.status && { 'filter[status]': filters.status }),
        ...(!!filters.channelId && { 'filter[channelId]': filters.channelId }),
        ...(!!filters.search && { 'filter[search]': filters.search }),
        ...(!!filters.dateFrom && { 'filter[dateFrom]': filters.dateFrom }),
        ...(!!filters.dateTo && { 'filter[dateTo]': filters.dateTo }),
      },
    });

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(dqCascadeQueryKeys.items(filters), fetchItems, {
    getNextPageParam: (lastPage) => lastPage.links.next,
    refetchInterval: (data) =>
      data?.pages.some((page) =>
        page.data.some(
          (item) => item.status === 'pending' || item.status === 'processing',
        ),
      )
        ? RUNNING_REFETCH_INTERVAL
        : false,
  });

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const pageOptions = useMemo<Option<number>[]>(
    () =>
      group.channels
        .map((channel) => ({ label: channel.name, value: channel.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [group.channels],
  );

  const onRetry = async (item: DQCascadeItemResource) => {
    try {
      setRetryingItemId(item.id);

      await api.post(`/v1/editorial/queue/dq-cascade/items/${item.id}/retry`);

      await queryClient.invalidateQueries(dqCascadeQueryKeys.all);

      alerts.success('Item queued for retry', item.channel.name);
    } catch (_) {
      alerts.error();
    } finally {
      setRetryingItemId(null);
    }
  };

  return (
    <>
      <Toolbar>
        {controls}
        <Controls>
          {STATUS_FILTERS.map((filter) => (
            <ControlsButton
              key={filter.label}
              gray
              blackText
              active={status === filter.value}
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </ControlsButton>
          ))}
        </Controls>
        <Filters>
          <SearchHolder>
            <DebouncedSearchBar
              setValue={setSearch}
              placeholder="Source post ID or link"
            />
          </SearchHolder>
          <PageSelect
            classNamePrefix="react-select"
            options={pageOptions}
            value={
              pageOptions.find((option) => option.value === channelId) ?? null
            }
            onChange={(option) =>
              setChannelId((option as Option<number> | null)?.value ?? null)
            }
            placeholder="All pages"
            isClearable
          />
          <DateRangeFilter
            selectedDateRange={dateRange}
            setSelectedDateRange={setDateRange}
            text="Run Date"
            allDaysSelectable
          />
          {!!run && (
            <Tag onDelete={onClearRun}>Run of {formatDate(run.runDate)}</Tag>
          )}
        </Filters>
      </Toolbar>
      <Body>
        <ItemsTableLayout>
          <thead>
            <tr>
              <Th>Source Post</Th>
              <Th>Page</Th>
              <Th>Run Date</Th>
              <Th>Status</Th>
              <Th>Processed</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              colSpan={6}
              isLoading={isLoading}
              isError={isError}
              hasData={!!items.length}
              emptyTableText="No items found"
            >
              {items.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <PostMessage>
                      {item.sourcePost.message || item.sourcePost.link || '-'}
                    </PostMessage>
                    <PostMeta>
                      #{item.sourcePost.id}
                      {!!item.sourcePost.publishedAt &&
                        ` · Published ${formatDate(
                          item.sourcePost.publishedAt,
                        )}`}
                      {!!item.sourcePost.link && (
                        <>
                          {' · '}
                          <a
                            href={item.sourcePost.link}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Article
                          </a>
                        </>
                      )}
                    </PostMeta>
                  </Td>
                  <Td>{item.channel.name}</Td>
                  <Td>{formatDate(item.run.runDate)}</Td>
                  <Td>
                    <ItemStatusBadge status={item.status} />
                    {(!!item.reason || !!item.error) && (
                      <StatusDetails>
                        {item.error ?? formatSkipReason(item.reason)}
                      </StatusDetails>
                    )}
                    {!!item.insertedPostId && (
                      <StatusDetails>
                        Queue post #{item.insertedPostId}
                      </StatusDetails>
                    )}
                  </Td>
                  <Td>
                    {formatDate(item.processedAt)}
                    <br />
                    <Text as="span" $color="gray" $type="extraSmall">
                      {formatTime(item.processedAt)}
                    </Text>
                  </Td>
                  <Td>
                    {item.status === 'failed' && (
                      <Button
                        small
                        color="red"
                        loading={retryingItemId === item.id}
                        disabled={retryingItemId !== null}
                        onClick={() => onRetry(item)}
                      >
                        Retry
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </TableContent>
          </tbody>
        </ItemsTableLayout>
        <InfiniteLoader
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </Body>
    </>
  );
};

export default ItemsTable;
